import { Injectable, computed, effect, inject } from '@angular/core';
import { getLangDesc, HttpCoreService } from '@dataclouder/ngx-core';
import { LangCodeDescription, LangCodeDescriptionEs } from '@dataclouder/ngx-lessons';
import { User, UserService } from '@dataclouder/ngx-users';

import { UserWebApi } from '../enums';
import { AGENT_CARDS_STATE_SERVICE, LESSONS_MASTER_STATE, WORDS_MASTER_STATE } from '../providers/knowledge.providers';
import { UtilsService } from '../utils.service';

export interface ActivityStats {
  conversations: number;
  learningSearches: number;
}
@Injectable({
  providedIn: 'root',
})
export class AppUserService extends UserService {
  private http = inject(HttpCoreService);
  private utilsService = inject(UtilsService);
  private wordsMasterState = inject(WORDS_MASTER_STATE);
  private lessonsMasterState = inject(LESSONS_MASTER_STATE);
  private agentCardsMasterState = inject(AGENT_CARDS_STATE_SERVICE);
  constructor() {
    super();
    console.log(`%c AppUserService instantiated: ${Math.random()}`, 'color: #ff00ff; font-weight: bold; padding: 2px 4px; border: 1px solid #ff00ff;');
    effect(() => {
      const targetLanguage = this.targetLanguage();
      if (targetLanguage) {
        this.wordsMasterState.setConfig({
          pathTable: `${targetLanguage}.word`,
          sourceCoreEndPoint: `api/word/query?target=${targetLanguage}`,
        });

        this.lessonsMasterState.setConfig({
          pathTable: `${targetLanguage}.lessons`,
          sourceCoreEndPoint: 'api/lesson/query',
        });
        this.agentCardsMasterState.setConfig({
          pathTable: `${targetLanguage}.agentCards`,
          sourceCoreEndPoint: 'api/agent-cards/query',
        });
      }
    });
  }

  // New computed signals for reactive language settings
  public targetLanguage = computed(() => this.user()?.settings?.targetLanguage);
  public baseLanguage = computed(() => this.user()?.settings?.baseLanguage);

  // Method to get the description of the target language
  public getTargetLanguageDescription = computed(() => {
    const langCode = this.targetLanguage();
    if (!langCode) return undefined;
    // Assuming LangCodeDescriptionEs is a simple key-value object
    return (LangCodeDescriptionEs as { [key: string]: string })[langCode];
  });

  // Method to get the description of the base language
  public getBaseLanguageDescription = computed(() => {
    const langCode = this.baseLanguage();
    if (!langCode) return undefined;
    // Assuming LangCodeDescription is a simple key-value object
    return (LangCodeDescription as { [key: string]: string })[langCode];
  });

  public async changeLanguage(lang: string) {
    try {
      const results = await this.http.post<User>(UserWebApi.ChangeLanguage, { lang });
      console.log('results', results);
      const user = { settings: results.settings, languageProgress: results.languageProgress, recommendations: results.recommendations };
      await this.updateLocalUser(user);
    } catch (err) {
      console.log('Was not able to update languages. results', err);
    }
  }

  public async getStats(): Promise<ActivityStats> {
    return this.http.get<ActivityStats>('user/stats');
  }

  public async updateLanguageProgress(langProgress: { lang: string; level: string }) {
    // TODO: not sure if i need this.
    const payload = { languageProgress: { [langProgress.lang]: { level: langProgress.level, lang: langProgress.lang } } };
    const data = await this.updatePartialUserServer(payload);
    console.log('results', data);
  }

  public getUserLangOptions() {
    const settings = this.user()?.settings;
    return this.utilsService.objectToQueryString({ base: settings.baseLanguage || 'es', target: settings.targetLanguage });
  }

  override getUserDataInformation(): string {
    const user = this.user();
    if (!user) {
      return '';
    }

    const settings = user.settings;
    const level = user.languageProgress[settings.targetLanguage]?.level;

    const dataDefinitions: { key: string; description: string; value: unknown }[] = [
      { key: 'name', description: 'Name of the user', value: user.personalData?.emotionalName || user.personalData?.firstname || 'Usuario' },
      { key: 'gender', description: 'Gender of the user', value: user.personalData?.gender },
      { key: 'baseLang', description: 'Base language of the user', value: getLangDesc(settings.baseLanguage, 'en') },
      { key: 'targetLang', description: 'Target language of the user', value: getLangDesc(settings.targetLanguage, 'en') },
      { key: 'level', description: 'Level of the user', value: level },
      // { key: 'levelDesc', description: 'Level description of the user', value: level ? LevelDescription[level] : undefined },
    ];

    if (user?.personalData?.birthday) {
      dataDefinitions.push({
        key: 'age',
        description: 'Age of the user',
        value: `${new Date().getFullYear() - new Date(user.personalData.birthday).getFullYear()}`,
      });
    }

    return dataDefinitions
      .filter((item) => item.value !== null && item.value !== undefined && item.value !== '')
      .map((item) => `[${item.description}: ${item.value}]`)
      .join('\n');
  }
}
